// src/components/account/SecuritySettings.js
import React, { useState } from 'react';
import {
  KeyIcon,
  EnvelopeIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { getAuth, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { useAuth } from '../../contexts/AuthContext';

const SecuritySettings = ({ data, onSave, saving }) => {
  const { currentUser } = useAuth();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordForm.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!passwordForm.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordForm.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordForm.newPassword)) {
      newErrors.newPassword = 'Password must contain uppercase, lowercase, and numbers';
    }

    if (!passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordChange = async () => {
    if (!validatePasswordForm()) return;

    setIsChangingPassword(true);
    setSuccessMessage('');
    
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      // Re-authenticate user
      const credential = EmailAuthProvider.credential(
        user.email,
        passwordForm.currentPassword
      );
      
      await reauthenticateWithCredential(user, credential);
      
      // Update password
      await updatePassword(user, passwordForm.newPassword);
      
      // Clear form and show success
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setSuccessMessage('Password updated successfully!');
      setErrors({});
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Error updating password:', error);
      if (error.code === 'auth/wrong-password') {
        setErrors({ currentPassword: 'Current password is incorrect' });
      } else if (error.code === 'auth/weak-password') {
        setErrors({ newPassword: 'Password is too weak' });
      } else {
        setErrors({ general: 'Failed to update password. Please try again.' });
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleInputChange = (field, value) => {
    setPasswordForm(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-l)' }}>
      {/* Success Message */}
      {successMessage && (
        <div style={{
          padding: 'var(--spacing-m)',
          backgroundColor: 'var(--Surface-Success)',
          borderLeft: '4px solid var(--Green-500)',
          borderRadius: 'var(--radius-xs)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-s)'
        }}>
          <p style={{
            fontSize: 'var(--font-size-body4)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--Green-700)'
          }}>{successMessage}</p>
        </div>
      )}

      {/* Error Message */}
      {errors.general && (
        <div style={{
          padding: 'var(--spacing-m)',
          backgroundColor: 'var(--Surface-Error)',
          borderLeft: '4px solid var(--Red-500)',
          borderRadius: 'var(--radius-xs)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-s)'
        }}>
          <p style={{
            fontSize: 'var(--font-size-body4)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--Text-Error)'
          }}>{errors.general}</p>
        </div>
      )}

      {/* Login Information */}
      <div>
        <div className="flex items-center" style={{ 
          gap: 'var(--spacing-s)',
          marginBottom: 'var(--spacing-xl)'
        }}>
          <div style={{ 
            padding: 'var(--spacing-xs)',
            borderRadius: 'var(--radius-xs)',
            background: 'var(--brand-gradient-2)'
          }}>
            <EnvelopeIcon className="h-5 w-5" style={{ color: 'var(--Text-Primary)' }} />
          </div>
          <h3 style={{
            fontSize: 'var(--font-size-subtitle2)',
            fontWeight: 'var(--font-weight-subtitle2)',
            color: 'var(--Text-Primary)'
          }}>Login Information</h3>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-m)' }}>
          <div>
            <label style={{
              display: 'block',
              fontSize: 'var(--font-size-body4)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--Text-Primary)',
              marginBottom: 'var(--spacing-xs)'
            }}>Login Email</label>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-s)',
              padding: 'var(--spacing-s)',
              backgroundColor: 'var(--Surface-Secondary)',
              borderRadius: 'var(--radius-xs)',
              border: '1px solid var(--Stroke-Light)'
            }}>
              <EnvelopeIcon className="h-5 w-5" style={{ color: 'var(--Text-Secondary)' }} />
              <span style={{
                color: 'var(--Text-Primary)',
                fontSize: 'var(--font-size-body3)'
              }}>
                {currentUser?.email || data?.loginEmail || 'Not available'}
              </span>
            </div>
            <p style={{
              fontSize: 'var(--font-size-body5)',
              color: 'var(--Text-Tertiary)',
              marginTop: 'var(--spacing-xxs)'
            }}>
              Your login email cannot be changed. Contact support if you need assistance.
            </p>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div style={{ borderTop: '1px solid var(--Stroke-Light)' }}></div>

      {/* Change Password */}
      <div>
        <div className="flex items-center" style={{ 
          gap: 'var(--spacing-s)',
          marginBottom: 'var(--spacing-xl)'
        }}>
          <div style={{ 
            padding: 'var(--spacing-xs)',
            borderRadius: 'var(--radius-xs)',
            background: 'var(--brand-gradient-2)'
          }}>
            <KeyIcon className="h-5 w-5" style={{ color: 'var(--Text-Primary)' }} />
          </div>
          <h3 style={{
            fontSize: 'var(--font-size-subtitle2)',
            fontWeight: 'var(--font-weight-subtitle2)',
            color: 'var(--Text-Primary)'
          }}>Change Password</h3>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-m)' }}>
          {/* Current Password */}
          <div>
            <label style={{
              display: 'block',
              fontSize: 'var(--font-size-body4)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--Text-Primary)',
              marginBottom: 'var(--spacing-xs)'
            }}>Current Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showCurrentPassword ? "text" : "password"}
                style={{
                  width: '100%',
                  padding: 'var(--input-padding-y) var(--input-padding-x)',
                  paddingRight: '48px',
                  fontSize: 'var(--font-size-body3)',
                  border: `1px solid ${errors.currentPassword ? 'var(--Stroke-Error)' : 'var(--Input-Border-Default)'}`,
                  borderRadius: 'var(--input-radius)',
                  backgroundColor: 'var(--Surface-Primary)',
                  color: 'var(--Text-Primary)',
                  transition: 'all 0.2s ease'
                }}
                value={passwordForm.currentPassword}
                onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                placeholder="Enter your current password"
                onFocus={(e) => {
                  if (!errors.currentPassword) {
                    e.target.style.borderColor = 'var(--Input-Border-Focused)';
                    e.target.style.boxShadow = 'var(--Input-Focus-Ring)';
                  }
                }}
                onBlur={(e) => {
                  if (!errors.currentPassword) {
                    e.target.style.borderColor = 'var(--Input-Border-Default)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              />
              <button
                type="button"
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--Text-Secondary)',
                  cursor: 'pointer',
                  padding: '4px',
                  transition: 'color 0.2s ease'
                }}
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--Text-Primary)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--Text-Secondary)'}
              >
                {showCurrentPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.currentPassword && (
              <p style={{
                fontSize: 'var(--font-size-body5)',
                color: 'var(--Text-Error)',
                marginTop: 'var(--spacing-xxs)'
              }}>{errors.currentPassword}</p>
            )}
          </div>

          {/* New Password */}
          <div>
            <label style={{
              display: 'block',
              fontSize: 'var(--font-size-body4)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--Text-Primary)',
              marginBottom: 'var(--spacing-xs)'
            }}>New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showNewPassword ? "text" : "password"}
                style={{
                  width: '100%',
                  padding: 'var(--input-padding-y) var(--input-padding-x)',
                  paddingRight: '48px',
                  fontSize: 'var(--font-size-body3)',
                  border: `1px solid ${errors.newPassword ? 'var(--Stroke-Error)' : 'var(--Input-Border-Default)'}`,
                  borderRadius: 'var(--input-radius)',
                  backgroundColor: 'var(--Surface-Primary)',
                  color: 'var(--Text-Primary)',
                  transition: 'all 0.2s ease'
                }}
                value={passwordForm.newPassword}
                onChange={(e) => handleInputChange('newPassword', e.target.value)}
                placeholder="Enter your new password"
                onFocus={(e) => {
                  if (!errors.newPassword) {
                    e.target.style.borderColor = 'var(--Input-Border-Focused)';
                    e.target.style.boxShadow = 'var(--Input-Focus-Ring)';
                  }
                }}
                onBlur={(e) => {
                  if (!errors.newPassword) {
                    e.target.style.borderColor = 'var(--Input-Border-Default)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              />
              <button
                type="button"
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--Text-Secondary)',
                  cursor: 'pointer',
                  padding: '4px',
                  transition: 'color 0.2s ease'
                }}
                onClick={() => setShowNewPassword(!showNewPassword)}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--Text-Primary)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--Text-Secondary)'}
              >
                {showNewPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.newPassword && (
              <p style={{
                fontSize: 'var(--font-size-body5)',
                color: 'var(--Text-Error)',
                marginTop: 'var(--spacing-xxs)'
              }}>{errors.newPassword}</p>
            )}
            <p style={{
              fontSize: 'var(--font-size-body5)',
              color: 'var(--Text-Tertiary)',
              marginTop: 'var(--spacing-xxs)'
            }}>
              Must be at least 8 characters with uppercase, lowercase, and numbers
            </p>
          </div>

          {/* Confirm Password */}
          <div>
            <label style={{
              display: 'block',
              fontSize: 'var(--font-size-body4)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--Text-Primary)',
              marginBottom: 'var(--spacing-xs)'
            }}>Confirm New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirmPassword ? "text" : "password"}
                style={{
                  width: '100%',
                  padding: 'var(--input-padding-y) var(--input-padding-x)',
                  paddingRight: '48px',
                  fontSize: 'var(--font-size-body3)',
                  border: `1px solid ${errors.confirmPassword ? 'var(--Stroke-Error)' : 'var(--Input-Border-Default)'}`,
                  borderRadius: 'var(--input-radius)',
                  backgroundColor: 'var(--Surface-Primary)',
                  color: 'var(--Text-Primary)',
                  transition: 'all 0.2s ease'
                }}
                value={passwordForm.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                placeholder="Confirm your new password"
                onFocus={(e) => {
                  if (!errors.confirmPassword) {
                    e.target.style.borderColor = 'var(--Input-Border-Focused)';
                    e.target.style.boxShadow = 'var(--Input-Focus-Ring)';
                  }
                }}
                onBlur={(e) => {
                  if (!errors.confirmPassword) {
                    e.target.style.borderColor = 'var(--Input-Border-Default)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              />
              <button
                type="button"
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--Text-Secondary)',
                  cursor: 'pointer',
                  padding: '4px',
                  transition: 'color 0.2s ease'
                }}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--Text-Primary)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--Text-Secondary)'}
              >
                {showConfirmPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p style={{
                fontSize: 'var(--font-size-body5)',
                color: 'var(--Text-Error)',
                marginTop: 'var(--spacing-xxs)'
              }}>{errors.confirmPassword}</p>
            )}
          </div>
        </div>

        {/* Update Password Button */}
        <div style={{ marginTop: 'var(--spacing-xl)' }}>
          <button
            onClick={handlePasswordChange}
            disabled={isChangingPassword}
            style={{
              padding: 'var(--button-padding-y) var(--button-padding-x)',
              background: isChangingPassword ? 'var(--Grey-200)' : 'var(--brand-gradient-1)',
              color: isChangingPassword ? 'var(--Text-Tertiary)' : 'var(--Text-Contrast)',
              border: 'none',
              borderRadius: 'var(--button-radius)',
              fontSize: 'var(--font-size-button)',
              fontWeight: 'var(--button-font-weight)',
              cursor: isChangingPassword ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--spacing-xs)',
              transition: 'all 0.2s ease',
              opacity: isChangingPassword ? 0.5 : 1
            }}
            onMouseEnter={(e) => {
              if (!isChangingPassword) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = 'var(--shadow-primary)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {isChangingPassword ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Updating...
              </>
            ) : (
              'Update Password'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;